import {
    AuthenticationDetails,
    CognitoUserPool,
    CognitoUser,
} from 'amazon-cognito-identity-js';
import { gql, Resolver } from '@apollo/client';
import _get from 'lodash/get';

import { getUserAttributes } from '@trqk/utils';

import { GET_SESSION } from './getSession';
import { GET_STATEMENTS_UI_STATE } from '../uiState/getStatementsUiState';

export interface Args {
    username: string;
    password: string;
    clientId: string;
    userPoolId: string;
    updateUiState: boolean;
}

export const signInWithCognito: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    { cache },
): Promise<SignInResult | WrappedCognitoError> => {
    const {
        username: usernameArg,
        password,
        clientId,
        userPoolId,
        updateUiState,
    } = args;
    const username = usernameArg.toLowerCase();

    const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
    });
    const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
    });
    const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
    });

    const authenticateUser = (): Promise<CognitoSessionResult> =>
        new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: result => {
                    const sessionIsValid = result.isValid();
                    // We used to use session.getAccessToken() to use the accessToken for auth,
                    // but now we're using the idToken. Both work from a client perspective; the
                    // backend uses some additional information (userId and email) encoded in the
                    // idToken to implement additional security logic.
                    const tokenInstance = result.getIdToken();
                    const token = tokenInstance.getJwtToken();
                    const expiration = tokenInstance.getExpiration();
                    const issuedAt = tokenInstance.getIssuedAt();

                    resolve({
                        token,
                        expiration,
                        issuedAt,
                        sessionIsValid,
                    });
                },
                onFailure: err => {
                    reject(err);
                },
            });
        });

    try {
        const result = await authenticateUser();
        const sessionIsValid = _get(result, 'sessionIsValid', false);
        const token = _get(result, 'token', null);
        const expiration = _get(result, 'expiration', null);
        const issuedAt = _get(result, 'issuedAt', null);

        // User is not authenticated; overwrite session and throw error
        if (!sessionIsValid) {
            cache.writeQuery({
                query: GET_SESSION,
                data: {
                    session: {
                        token,
                        codeDeliveryDetails: null,
                        currentUser: null,
                        expiration,
                        isSigningUp: false,
                        issuedAt,
                        sessionIsValid,
                        __typename: 'Session',
                    },
                },
            });

            throw new Error('Invalid session');
        } else {
            // User is authenticated, proceed

            const getUserAttributesResult = await getUserAttributes(
                cognitoUser,
            );

            if (getUserAttributesResult instanceof Error) {
                throw getUserAttributesResult;
            }

            const {
                trqkUserId,
                statementId,
                name,
                username: usernameAttribute,
                email,
            } = getUserAttributesResult;

            // .getUsername() actually returns the userSub from what I can tell by testing manually.
            const userSub = cognitoUser.getUsername();
            const isSigningUp = false;

            cache.writeQuery({
                query: GET_SESSION,
                data: {
                    session: {
                        token,
                        codeDeliveryDetails: null,
                        currentUser: {
                            id: trqkUserId ?? userSub,
                            userSub,
                            email,
                            name: name ?? '',
                            statementId: statementId ?? '',
                            username: usernameAttribute,
                            __typename: 'User',
                        },
                        expiration,
                        isSigningUp,
                        issuedAt,
                        sessionIsValid,
                        __typename: 'Session',
                    },
                },
            });

            if (updateUiState) {
                cache.writeQuery({
                    query: GET_STATEMENTS_UI_STATE,
                    data: {
                        uiState: {
                            statements: {
                                statementId,
                                __typename: 'Statements',
                            },
                            __typename: 'UIState',
                        },
                    },
                });
            }

            return {
                token,
                email,
                name,
                sessionIsValid,
                trqkUserId,
                username: usernameAttribute,
            };
        }
    } catch (err) {
        return { error: err };
    }
};

export const SIGN_IN_WITH_COGNITO = gql`
    mutation SignInWithCognito(
        $username: String
        $password: String
        $clientId: String
        $userPoolId: String
        $updateUiState: Boolean
    ) {
        signInWithCognito(
            username: $username
            password: $password
            clientId: $clientId
            userPoolId: $userPoolId
            updateUiState: $updateUiState
        ) @client
    }
`;
